/**
 * v2 stream reducer — pure, deterministic accumulator→snapshot fold.
 *
 * Designed for Phase-1 Vue stores to consume instead of the legacy `adaptEvent`.
 * Pure TypeScript, zero Vue / framework dependency (CLAUDE.md §3.1 red line).
 *
 * ## Seq / ordering policy
 * - Each event carries a globally monotonic `seq` cursor.
 * - On `run.start` the current message's `lastSeq` resets to `event.seq - 1` so
 *   all subsequent events for this run are accepted.
 * - For all other events: if `event.seq <= state.lastSeq`, the event is SKIPPED
 *   (duplicate / out-of-order replay already seen).
 * - `lastSeq` is updated to `event.seq` after every accepted event.
 * - Delta accumulation is idempotent given strict seq ordering; `*.end` events
 *   are authoritative no-op finalizers (they carry no new content).
 * - Unknown `type` values are silently ignored (forward-compat; never throw).
 *
 * ## Immutability contract
 * `reduce` returns a new `ReduceState` object. Top-level arrays (messages, parts)
 * are replaced on mutation so shallow comparison works in Vue reactive systems.
 * Inner objects (e.g. `TextPart`) are also replaced on mutation.
 */
import { coerceArtifactRef } from '../../artifact/contracts'
import type { PublicArtifactRef } from '../../artifact/contracts'

import type {
  StreamEvent,
  Usage,
  FinishReason,
  ToolOutput,
  ToolFailureDiagnostic,
  ToolStatus,
  RiskLevel,
  JsonPatchOp,
  CardData,
  CardKind,
  CardPlacement,
  JsonObject,
  PlanTraceTask,
  ClarifyQuestion,
  ProgressStatus,
  DshPresentationProjection,
} from '../../protocol/v2'

// ---------------------------------------------------------------------------
// Snapshot types (public API)
// ---------------------------------------------------------------------------

/** Status of an assistant message lifetime (v2 reducer). */
export type RunStatus = 'streaming' | 'complete' | 'cancelled' | 'error'

/** Status of a tool call across its state machine. */
export type ToolPartStatus =
  | 'pending'
  | 'input-streaming'
  | 'input-ready'
  | 'executing'
  | 'output-available'
  | 'output-error'

/** A streamed text block (main reply or continuation). */
export interface TextPart {
  kind: 'text'
  partId: string
  text: string
  provisional?: boolean
}

/** Safe public analysis summary. Raw provider chain-of-thought is never materialized. */
export interface ReasoningPart {
  kind: 'reasoning'
  partId: string
  text: string
}

/** A tool invocation with full lifecycle state. */
export interface ToolPart {
  kind: 'tool'
  toolCallId: string
  toolName: string
  status: ToolPartStatus
  /** Correlation id shared with the result cards / stages of this tool call (v2.1). */
  group?: string
  /** Accumulated raw JSON string of the input (from `tool.input.delta`). */
  inputRaw?: string
  /** Authoritative parsed input (from `tool.input`). */
  input?: JsonObject
  output?: ToolOutput
  outputStatus?: ToolStatus
  error?: string
  diagnostic?: ToolFailureDiagnostic
}

/** A rich presentation card (table / chart / metric / …). */
export interface CardPart {
  kind: 'card'
  id: string
  cardKind: CardKind
  data: CardData
  /** Correlation id shared with the tool / sibling cards of one result (v2.1). */
  group?: string
  /** Section placement hint for Process / Answer / Artifact rendering. */
  placement?: CardPlacement
  /** Optional answer anchor this card is attached to. */
  anchorId?: string
  transient?: boolean
  status?: string
}

/** Authoritative presentation event metadata retained alongside renderable cards. */
export interface PresentationPart {
  kind: 'presentation'
  projection: DshPresentationProjection
}

/**
 * Delivery artifact hint materialized from `presentation.attachments`.
 * Idempotent per `artifact.artifactId` so reconnect replays never duplicate.
 */
export interface ArtifactPart {
  kind: 'artifact'
  artifact: PublicArtifactRef
}

/** A deterministic pipeline stage progress notification (v2.1 additive). */
export interface StagePart {
  kind: 'stage'
  stageId: string
  /** Source event sequence; current-stage selection must use this, not array order. */
  seq?: number
  label: string
  /** Open-enum phase hint, e.g. 'schema-linking' | 'dsl-gen' | 'execute'. */
  phase?: string
  progress?: number
  /** Correlation id shared with the producing tool / cards (v2.1). */
  group?: string
  /** Stable vNext identity; legacy stage events omit it. */
  progressId?: string
  /** Stable capability / plan step identity. */
  stepId?: string
  status?: ProgressStatus
  attempt?: number
  elapsedMs?: number
  safeSummary?: string
  occurredAt?: string
}

/** Latest backend-owned execution-plan snapshot for this turn. */
export interface PlanPart {
  kind: 'plan'
  runId: string
  messageId: string
  planId: string
  planName: string
  state: string
  version: number
  tasks: PlanTraceTask[]
}

/** A write-action pending 二次确认. */
export interface ActionPart {
  kind: 'action'
  actionId: string
  title: string
  summary?: string
  riskLevel: RiskLevel
  params: Record<string, string | number | boolean | null>
}

/** A clarification request asking the user to choose or answer. */
export interface ClarifyPart {
  kind: 'clarify'
  requestId: string
  questions: ClarifyQuestion[]
}

/** Discriminated union of all renderable parts of an assistant turn. */
export type Part =
  | TextPart
  | ReasoningPart
  | ToolPart
  | CardPart
  | StagePart
  | PlanPart
  | ActionPart
  | ClarifyPart
  | PresentationPart
  | ArtifactPart

/** Turn-level AskData observability restored from backend v2 event metadata. */
export interface AskDataObservation {
  traceId?: string
  runId?: string
  stageLatenciesMs?: Record<string, number>
  schemaConfidence?: number
  goldenHit?: boolean
  retryReason?: string
  rowCount?: number
  denyReason?: string
  sensitive?: boolean
  [key: string]: unknown
}

/**
 * Complete snapshot of a single assistant message at a point in time.
 * Parts are ordered by the `seq` of their originating start event.
 */
export interface MessageSnapshot {
  messageId: string
  conversationId: string
  status: RunStatus
  parts: Part[]
  usage?: Usage
  finishReason?: FinishReason
  /** Wall-clock latency reported by the backend on `run.finish`. */
  latencyMs?: number
  /** AskData turn observability restored from persisted v2 event metadata. */
  observation?: AskDataObservation
  /** Backend run error metadata, preserved so hosts can show retry affordances. */
  error?: {
    code: string
    message: string
    retriable: boolean
  }
  /** Timestamp (ms) when `run.start` was received. */
  startedAt: number
}

// ---------------------------------------------------------------------------
// Index maps (kept inside state for O(1) lookups)
// ---------------------------------------------------------------------------

/** Internal indexes for the current (last) message. */
interface MessageIndexes {
  /** partId → latest writable index in `parts` for text/reasoning parts */
  byPartId: Map<string, number>
  /** toolCallId → index in `parts` for tool parts */
  byToolCallId: Map<string, number>
  /** card.id → index in `parts` for card parts */
  byCardId: Map<string, number>
  /** planId -> index in `parts`; later snapshots replace the first one in place. */
  byPlanId: Map<string, number>
  /** requestId -> clarification index; repeated input.required updates in place. */
  byRequestId: Map<string, number>
}

/**
 * Accumulator state for `reduce`. Plain / serialisable (Maps are the only
 * non-JSON-native field; callers that need JSON-round-trip should serialise
 * them separately).
 */
export interface ReduceState {
  /** Ordered list of assistant messages produced so far. */
  messages: MessageSnapshot[]
  /** Last `seq` accepted (used for duplicate/OOO filtering). */
  lastSeq: number
  /**
   * Internal indexes for the current (last) message.
   * Replaced whenever the active message changes.
   * @internal — Vue stores should not depend on this directly.
   */
  _idx: MessageIndexes
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeIndexes(): MessageIndexes {
  return {
    byPartId: new Map(),
    byToolCallId: new Map(),
    byCardId: new Map(),
    byPlanId: new Map(),
    byRequestId: new Map(),
  }
}

/** Return a shallow-cloned state with `messages` array replaced. */
function withMessages(state: ReduceState, messages: MessageSnapshot[]): ReduceState {
  return { ...state, messages }
}

/** Replace the last message in the array. */
function replaceLast(messages: MessageSnapshot[], next: MessageSnapshot): MessageSnapshot[] {
  if (messages.length === 0) return messages
  return [...messages.slice(0, -1), next]
}

/** Return the last message, or undefined. */
function lastMsg(state: ReduceState): MessageSnapshot | undefined {
  return state.messages[state.messages.length - 1]
}

/** Replace a part at `index` in the parts array. */
function replacePart(parts: Part[], index: number, next: Part): Part[] {
  const arr = [...parts]
  arr[index] = next
  return arr
}

function isWritableTextPart(msg: MessageSnapshot, index: number): boolean {
  return index === msg.parts.length - 1 && msg.parts[index]?.kind === 'text'
}

/**
 * Apply a minimal subset of RFC 6902 JSON Patch (add / remove / replace) to a
 * plain object. Path must be a single-level `/key` reference. Nested paths and
 * arrays are ignored silently (forward-compat).
 */
function applyPatch(
  target: Record<string, unknown>,
  ops: JsonPatchOp[],
): Record<string, unknown> {
  let result = { ...target }
  for (const op of ops) {
    // Only handle single-level paths: "/key"
    const match = /^\/([^/]+)$/.exec(op.path)
    if (!match) continue
    const key = match[1]
    if (op.op === 'remove') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _removed, ...rest } = result
      result = rest
    } else if (op.op === 'add' || op.op === 'replace') {
      result = { ...result, [key]: op.value }
    }
  }
  return result
}

const TOOL_STATUS_RANK: Record<ToolPartStatus, number> = {
  pending: 0,
  'input-streaming': 1,
  'input-ready': 2,
  executing: 3,
  'output-available': 4,
  'output-error': 4,
}

function mergeToolPart(current: ToolPart, next: ToolPart): ToolPart {
  const status =
    TOOL_STATUS_RANK[next.status] >= TOOL_STATUS_RANK[current.status]
      ? next.status
      : current.status

  return {
    ...current,
    ...next,
    status,
    group: next.group ?? current.group,
    inputRaw: next.inputRaw ?? current.inputRaw,
    input: next.input ?? current.input,
    output: next.output ?? current.output,
    outputStatus: next.outputStatus ?? current.outputStatus,
    error: next.error ?? current.error,
  }
}

// ---------------------------------------------------------------------------
// initialState
// ---------------------------------------------------------------------------

/** Factory for a blank accumulator. */
export function initialState(): ReduceState {
  return {
    messages: [],
    lastSeq: 0,
    _idx: makeIndexes(),
  }
}

// ---------------------------------------------------------------------------
// reduce
// ---------------------------------------------------------------------------

/**
 * Pure fold: given the current accumulator state and one v2 `StreamEvent`,
 * return the next state. Never mutates `state`. Unknown event types are
 * ignored silently.
 */
export function reduce(state: ReduceState, event: StreamEvent): ReduceState {
  // Duplicate / out-of-order guard (except run.start which resets the cursor)
  if (event.type !== 'run.start' && event.seq <= state.lastSeq) {
    return state
  }

  // Advance seq (will be done for every accepted event below)
  const nextSeq = event.seq

  // Shorthand: get the current message and its parts
  const msg = lastMsg(state)

  switch (event.type) {
    // -------------------------------------------------------------------------
    // run lifecycle
    // -------------------------------------------------------------------------
    case 'run.start': {
      const newMsg: MessageSnapshot = {
        messageId: event.messageId,
        conversationId: event.conversationId,
        status: 'streaming',
        parts: [],
        startedAt: Date.now(),
      }
      return {
        messages: [...state.messages, newMsg],
        lastSeq: nextSeq,
        _idx: makeIndexes(),
      }
    }

    case 'run.finish': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const finished: MessageSnapshot = {
        ...msg,
        parts: clearPreviews(msg.parts),
        status: event.reason === 'cancelled' ? 'cancelled' : 'complete',
        usage: event.usage,
        finishReason: event.reason,
        latencyMs: event.latencyMs,
      }
      return {
        ...withMessages(state, replaceLast(state.messages, finished)),
        lastSeq: nextSeq,
      }
    }

    case 'run.error': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const errored: MessageSnapshot = {
        ...msg,
        parts: clearPreviews(msg.parts),
        status: 'error',
        error: {
          code: event.code,
          message: event.message,
          retriable: event.retriable,
        },
      }
      return {
        ...withMessages(state, replaceLast(state.messages, errored)),
        lastSeq: nextSeq,
      }
    }

    // -------------------------------------------------------------------------
    // text block
    // -------------------------------------------------------------------------
    case 'text.start': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      if (hasCompletePresentationLayout(msg)) return { ...state, lastSeq: nextSeq }
      const idx = msg.parts.length
      const newPart: TextPart = { kind: 'text', partId: event.partId, text: '' }
      const nextIdx = { ...state._idx, byPartId: new Map(state._idx.byPartId) }
      nextIdx.byPartId.set(event.partId, idx)
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    case 'text.delta': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      if (hasCompletePresentationLayout(msg)) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byPartId.get(event.partId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      if (!isWritableTextPart(msg, idx)) {
        const newIdx = msg.parts.length
        const nextIdx = { ...state._idx, byPartId: new Map(state._idx.byPartId) }
        nextIdx.byPartId.set(event.partId, newIdx)
        const newPart: TextPart = { kind: 'text', partId: event.partId, text: event.delta }
        const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
        return {
          ...withMessages(state, replaceLast(state.messages, nextMsg)),
          lastSeq: nextSeq,
          _idx: nextIdx,
        }
      }
      const part = msg.parts[idx] as TextPart
      const updated: TextPart = { ...part, text: part.text + event.delta }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'text.end': {
      // Authoritative no-op finalizer — just advance seq
      return { ...state, lastSeq: nextSeq }
    }

    // -------------------------------------------------------------------------
    // safe public execution narration
    // -------------------------------------------------------------------------
    case 'narration.start': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = msg.parts.length
      const newPart: ReasoningPart = { kind: 'reasoning', partId: event.partId, text: '' }
      const nextIdx = { ...state._idx, byPartId: new Map(state._idx.byPartId) }
      nextIdx.byPartId.set(event.partId, idx)
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    case 'narration.delta': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byPartId.get(event.partId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx]
      if (!part || part.kind !== 'reasoning') return { ...state, lastSeq: nextSeq }
      const updated: ReasoningPart = { ...part, text: part.text + event.delta }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'narration.end': {
      return { ...state, lastSeq: nextSeq }
    }

    // -------------------------------------------------------------------------
    // Legacy reasoning block
    // -------------------------------------------------------------------------
    case 'reasoning.start':
    case 'reasoning.delta':
    case 'reasoning.end': {
      // Rolling-upgrade compatibility: old v2 frames remain parseable so their
      // cursors can advance, but raw provider reasoning is never materialized.
      return { ...state, lastSeq: nextSeq }
    }

    // -------------------------------------------------------------------------
    // tool-call state machine
    // -------------------------------------------------------------------------
    case 'tool.start': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const newPart: ToolPart = {
        kind: 'tool',
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        status: 'pending',
        group: event.group,
      }
      const existing = state._idx.byToolCallId.get(event.toolCallId)
      if (existing !== undefined) {
        const part = msg.parts[existing] as ToolPart
        const updated = mergeToolPart(part, newPart)
        const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, existing, updated) }
        return {
          ...withMessages(state, replaceLast(state.messages, nextMsg)),
          lastSeq: nextSeq,
        }
      }
      const idx = msg.parts.length
      const nextIdx = { ...state._idx, byToolCallId: new Map(state._idx.byToolCallId) }
      nextIdx.byToolCallId.set(event.toolCallId, idx)
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    case 'tool.input.delta': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byToolCallId.get(event.toolCallId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as ToolPart
      const updated: ToolPart = {
        ...part,
        status: 'input-streaming',
        inputRaw: (part.inputRaw ?? '') + event.delta,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'tool.input': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byToolCallId.get(event.toolCallId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as ToolPart
      const updated: ToolPart = {
        ...part,
        status: 'input-ready',
        input: event.input,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'tool.executing': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byToolCallId.get(event.toolCallId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as ToolPart
      const updated: ToolPart = { ...part, status: 'executing' }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'tool.output': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byToolCallId.get(event.toolCallId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as ToolPart
      const updated: ToolPart = {
        ...part,
        status: event.status === 'output-available' ? 'output-available' : 'output-error',
        output: event.output,
        outputStatus: event.status,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'tool.error': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byToolCallId.get(event.toolCallId)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as ToolPart
      const updated: ToolPart = {
        ...part,
        status: 'output-error',
        error: event.error,
        diagnostic: event.diagnostic,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    case 'answer.preview': {
      if (!msg || msg.status !== 'streaming' || hasCompletePresentationLayout(msg)) return { ...state, lastSeq: nextSeq }
      const partId = `answer-preview:${event.previewId}`
      const parts = [...msg.parts]
      const index = parts.findIndex(part => part.kind === 'text' && part.partId === partId)
      const part: TextPart = { kind: 'text', partId, text: event.text, provisional: true }
      if (index < 0) parts.push(part)
      else parts[index] = part
      return { ...withMessages(state, replaceLast(state.messages, { ...msg, parts })), lastSeq: nextSeq }
    }

    case 'presentation': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const presentation: PresentationPart = {
        kind: 'presentation',
        projection: event.payload,
      }
      const cardParts: CardPart[] = event.payload.cards.map((card, cardIndex) => {
        // Table and chart cards derived from the same governed evidence are
        // complementary views of one result. Give them a shared base group so
        // the renderer can present a single 图/表 switch instead of rendering
        // the chart's accessible data table and an identical standalone table.
        // Distinct narrative anchors are still split by groupTurnSections.
        const presentationGroup = `presentation:${event.seq}:${card.evidenceId}`
        // A stat card stands alone: there is no underlying table to offer as a
        // second view, so it carries only its own label and value.
        if (card.kind === 'metric') {
          return {
            kind: 'card',
            id: `${card.artifactId}:metric:${cardIndex}`,
            cardKind: 'metric',
            group: presentationGroup,
            placement: 'answer',
            data: {
              kind: 'metric',
              evidenceId: card.evidenceId,
              items: [{
                label: card.label,
                value: card.value,
                ...(card.unit === undefined ? {} : { unit: card.unit }),
              }],
            },
          }
        }
        const columns = card.columns.map(key => ({
          key,
          title: card.columnLabels?.[key] || key,
          displayName: card.columnLabels?.[key] || key,
        }))
        const rows = card.previewRows.map(values => Object.fromEntries(
          card.columns.map((column, index) => [column, values[index] ?? null]),
        ))
        if (card.kind === 'chart') {
          return {
            kind: 'card',
            id: `${card.artifactId}:chart:${cardIndex}`,
            cardKind: 'chart',
            group: presentationGroup,
            placement: 'answer',
            data: {
              kind: 'chart',
              rowCount: card.rowCount,
              truncated: card.truncated,
              evidenceId: card.evidenceId,
              datasetId: card.datasetId,
              artifactId: card.artifactId,
              spec: {
                chartKind: card.chartKind,
                title: card.title,
                datasetRef: card.datasetId,
                encoding: {
                  x: card.xColumn,
                  y: card.yColumns,
                  series: card.seriesColumn,
                  ...(card.radarMax === undefined ? {} : { radarMax: card.radarMax }),
                  ...(card.lineColumns === undefined ? {} : { lineColumns: card.lineColumns }),
                  ...(card.axisUnits === undefined ? {} : { axisUnits: card.axisUnits }),
                },
                columnLabels: card.columnLabels,
                data: rows,
              },
            },
          }
        }
        return {
          kind: 'card',
          id: `${card.artifactId}:table:${cardIndex}`,
          cardKind: 'table',
          group: presentationGroup,
          placement: 'answer',
          data: {
            kind: 'table',
            columns,
            rows,
            total: card.rowCount,
            truncated: card.truncated,
            evidenceId: card.evidenceId,
            datasetId: card.datasetId,
            artifactId: card.artifactId,
          },
        }
      })
      const inlineLayout = isCompletePresentationLayout(event.payload)
        ? event.payload.layout
        : undefined
      const renderableParts: Part[] = []
      let currentAnchorId: string | undefined
      if (inlineLayout !== undefined) {
        inlineLayout.forEach((block, layoutIndex) => {
          if (block.kind === 'markdown') {
            currentAnchorId = `presentation:${event.seq}:markdown:${layoutIndex}`
            renderableParts.push({
              kind: 'text',
              partId: currentAnchorId,
              text: block.text,
            })
            return
          }
          const card = cardParts[block.cardIndex]!
          renderableParts.push({
            ...card,
            ...(currentAnchorId === undefined ? {} : { anchorId: currentAnchorId }),
          })
        })
      } else {
        renderableParts.push(...cardParts)
      }
      const webSources = (event.payload.sources ?? []).flatMap((raw) => {
        const source = coerceWebSource(raw)
        return source === undefined ? [] : [source]
      })
      const sourceParts: CardPart[] = webSources.length === 0 ? [] : [{
        kind: 'card',
        id: `presentation:${event.seq}:web-sources`,
        cardKind: 'source',
        group: `presentation:${event.seq}:web-sources`,
        placement: 'answer',
        data: {
          kind: 'source',
          items: webSources.map(source => ({ kind: 'web' as const, ...source })),
        },
      }]
      const artifactParts: Part[] = []
      for (const raw of event.payload.attachments ?? []) {
        const ref = coerceArtifactRef(raw)
        if (!ref) {
          console.warn('[agentic-core] presentation attachment failed validation; skipped', { seq: event.seq })
          continue
        }
        const previousIndex = artifactParts.findIndex(
          part => part.kind === 'artifact' && part.artifact.artifactId === ref.artifactId,
        )
        if (previousIndex >= 0) artifactParts.splice(previousIndex, 1, { kind: 'artifact', artifact: ref })
        else artifactParts.push({ kind: 'artifact', artifact: ref })
      }
      const start = msg.parts.length
      const nextIdx = { ...state._idx, byCardId: new Map(state._idx.byCardId) }
      const answerParts = [...renderableParts, ...sourceParts]
      answerParts.forEach((part, index) => {
        if (part.kind === 'card') nextIdx.byCardId.set(part.id, start + 1 + index)
      })
      const nextMsg: MessageSnapshot = {
        ...msg,
        parts: [...clearPreviews(msg.parts), presentation, ...answerParts, ...artifactParts],
      }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    // -------------------------------------------------------------------------
    // card (upsert by id; later overrides earlier: loading→ready)
    // -------------------------------------------------------------------------
    case 'card': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const existing = state._idx.byCardId.get(event.id)
      const newPart: CardPart = {
        kind: 'card',
        id: event.id,
        cardKind: event.kind,
        data: event.data,
        group: event.group,
        placement: event.placement,
        anchorId: event.anchorId,
        transient: event.transient,
        status: event.status,
      }
      if (existing !== undefined) {
        // Upsert: replace in place
        const nextMsg: MessageSnapshot = {
          ...msg,
          parts: replacePart(msg.parts, existing, newPart),
        }
        return {
          ...withMessages(state, replaceLast(state.messages, nextMsg)),
          lastSeq: nextSeq,
        }
      }
      // New card: append
      const idx = msg.parts.length
      const nextIdx = { ...state._idx, byCardId: new Map(state._idx.byCardId) }
      nextIdx.byCardId.set(event.id, idx)
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    case 'card.patch': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const idx = state._idx.byCardId.get(event.id)
      if (idx === undefined) return { ...state, lastSeq: nextSeq }
      const part = msg.parts[idx] as CardPart
      // Apply patch to CardData (cast through unknown for type safety)
      const patchedData = applyPatch(
        part.data as unknown as Record<string, unknown>,
        event.patch,
      ) as unknown as CardData
      const updated: CardPart = { ...part, data: patchedData }
      const nextMsg: MessageSnapshot = { ...msg, parts: replacePart(msg.parts, idx, updated) }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    // -------------------------------------------------------------------------
    // stage (deterministic pipeline progress; append in order, no de-dup)
    // -------------------------------------------------------------------------
    case 'stage': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const newPart: StagePart = {
        kind: 'stage',
        stageId: event.stageId,
        seq: event.seq,
        label: event.label,
        phase: event.phase,
        progress: event.progress,
        group: event.group,
        progressId: event.progressId,
        stepId: event.stepId,
        status: event.status,
        attempt: event.attempt,
        elapsedMs: event.elapsedMs,
        safeSummary: event.safeSummary,
        occurredAt: event.occurredAt,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    // -------------------------------------------------------------------------
    // plan trace (the backend emits a full, versioned snapshot on every change)
    // -------------------------------------------------------------------------
    case 'plan.trace': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const newPart: PlanPart = {
        kind: 'plan',
        runId: event.runId,
        messageId: event.messageId,
        planId: event.planId,
        planName: event.planName,
        state: event.state,
        version: event.version,
        tasks: event.tasks,
      }
      const existing = state._idx.byPlanId.get(event.planId)
      if (existing !== undefined) {
        const current = msg.parts[existing] as PlanPart
        if (event.version < current.version) {
          return { ...state, lastSeq: nextSeq }
        }
        const nextMsg: MessageSnapshot = {
          ...msg,
          parts: replacePart(msg.parts, existing, newPart),
        }
        return {
          ...withMessages(state, replaceLast(state.messages, nextMsg)),
          lastSeq: nextSeq,
        }
      }

      const idx = msg.parts.length
      const nextIdx = { ...state._idx, byPlanId: new Map(state._idx.byPlanId) }
      nextIdx.byPlanId.set(event.planId, idx)
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    // -------------------------------------------------------------------------
    // action center
    // -------------------------------------------------------------------------
    case 'action.proposed': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const newPart: ActionPart = {
        kind: 'action',
        actionId: event.actionId,
        title: event.title,
        summary: event.summary,
        riskLevel: event.riskLevel,
        params: event.params,
      }
      const nextMsg: MessageSnapshot = { ...msg, parts: [...msg.parts, newPart] }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
      }
    }

    // -------------------------------------------------------------------------
    // clarify
    // -------------------------------------------------------------------------
    case 'clarify': {
      if (!msg) return { ...state, lastSeq: nextSeq }
      const newPart: ClarifyPart = {
        kind: 'clarify',
        requestId: event.requestId,
        questions: event.questions,
      }
      const existingIndex = state._idx.byRequestId.get(event.requestId)
      const parts = existingIndex === undefined
        ? [...msg.parts, newPart]
        : replacePart(msg.parts, existingIndex, newPart)
      const nextIdx = existingIndex === undefined
        ? { ...state._idx, byRequestId: new Map(state._idx.byRequestId) }
        : state._idx
      if (existingIndex === undefined) {
        nextIdx.byRequestId.set(event.requestId, msg.parts.length)
      }
      const nextMsg: MessageSnapshot = { ...msg, parts }
      return {
        ...withMessages(state, replaceLast(state.messages, nextMsg)),
        lastSeq: nextSeq,
        _idx: nextIdx,
      }
    }

    // -------------------------------------------------------------------------
    // Forward-compat: unknown event types are silently ignored (never throw)
    // -------------------------------------------------------------------------
    default: {
      // TypeScript exhaustiveness: this branch is unreachable with known events,
      // but at runtime unknown future types fall through safely.
      return { ...state, lastSeq: nextSeq }
    }
  }
}

function hasCompletePresentationLayout(message: MessageSnapshot): boolean {
  return message.parts.some(part =>
    part.kind === 'presentation' && isCompletePresentationLayout(part.projection))
}

function clearPreviews(parts: Part[]): Part[] {
  return parts.map(part => part.kind === 'text' && part.provisional ? { ...part, text: '' } : part)
}

function isCompletePresentationLayout(projection: DshPresentationProjection): boolean {
  const layout = projection.layout
  if (layout === undefined || layout.length === 0 || layout[0]?.kind !== 'markdown') return false
  const cardIndexes = new Set<number>()
  for (const block of layout) {
    if (block.kind === 'markdown') {
      if (block.text.trim().length === 0) return false
      continue
    }
    if (!Number.isSafeInteger(block.cardIndex)
      || block.cardIndex < 0
      || block.cardIndex >= projection.cards.length
      || cardIndexes.has(block.cardIndex)) return false
    cardIndexes.add(block.cardIndex)
  }
  return cardIndexes.size === projection.cards.length
}

function coerceWebSource(value: unknown): {
  url: string
  title?: string
  snippet?: string
  publishedAt?: string
} | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined
  const source = value as Record<string, unknown>
  if (typeof source.url !== 'string' || source.url.length > 2_048) return undefined
  try {
    const parsed = new URL(source.url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined
  } catch {
    return undefined
  }
  const optional = [
    ['title', 512], ['snippet', 4_000], ['publishedAt', 128],
  ] as const
  const result: { url: string; title?: string; snippet?: string; publishedAt?: string } = {
    url: source.url,
  }
  for (const [key, maximum] of optional) {
    const candidate = source[key]
    if (candidate === undefined) continue
    if (typeof candidate !== 'string' || candidate.trim().length === 0
      || candidate.length > maximum) return undefined
    result[key] = candidate
  }
  return result
}

// ---------------------------------------------------------------------------
// reduceAll
// ---------------------------------------------------------------------------

/**
 * Convenience fold: reduce an entire ordered sequence of events from scratch.
 * Equivalent to `events.reduce(reduce, initialState())`.
 */
export function reduceAll(events: StreamEvent[]): ReduceState {
  return events.reduce(reduce, initialState())
}
